import { useState } from 'react';
import { Drawer, useTranslation, useFeatureVisibility } from '@nextgen-web-framework/all';
import {
  AmwayUserDetails,
  Cart,
  CartEventsEnum,
  OrderType,
  PublishEvent,
  SWITCH_PROXY_TYPE,
  getActiveCart,
  isGroupOrder,
  isProxyOrder,
  switchCart,
  switchOrOverrideCart,
  switchToRegularCart,
  updateCart,
  updateShippingAddressInLS,
  updateShippingAddressToEmpty,
} from '@commerce-cart-webcomponents/sdk';
import { useAddressUpdate } from '@commerce-webcomponents/ui';
import { useDispatch } from 'react-redux';
import { AccountSearchDrawer } from '../../../common/components/account-search-drawer/AccountSearchDrawer';
import { CartNotificationTheme, TOAST_TIMEOUT, useCartNotification, useExceptionToast } from '../../../common';
import styles from './CartTypeSelectionDrawer.module.scss';
import { CartTypeSelectionDrawerProps } from './types/propsType';
import { CartTypeSelection } from './cart-type-selection/CartTypeSelection';

export const CartTypeSelectionDrawer = ({
  onClose,
  initalOrderType,
  onRadioChange,
  cartData,
  toggleDisplayAccountSearch,
  toggleDrawer,
  displayAccountSearch = false,
  onSwitchSuccess,
}: CartTypeSelectionDrawerProps) => {
  const t = useTranslation();
  const { updateAddress } = useAddressUpdate();
  const { createToast, deleteToast } = useCartNotification('cartTypeSelectionDrawer');
  const dispatch = useDispatch();
  const [showAccountSearch, setShowAccountSearch] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AmwayUserDetails>();
  const cartForText = t('cart.cartTypeSelectionDrawer.cartFor');
  const cartOverrideFlag = useFeatureVisibility?.('cart.cartoverride') ?? true;

  const searchDrawerHeaderTitle = t('cart.cartTypeSelectionDrawer.drawerHeader');

  const inputPlaceHolderText = t('cart.cartTypeSelectionDrawer.inputPlaceHolder');
  const primaryButtonText = t('cart.cartTypeSelectionDrawer.primaryButtonText');
  const secondaryButtonText = t('cart.cartTypeSelectionDrawer.secondaryButtonText');
  const subHeadingText = t('cart.cartTypeSelectionDrawer.subHeading');
  const cartChanged = t('cart.cartTypeSelectionDrawer.cartChanged');
  const cartChangedHeading = t('cart.cartTypeSelectionDrawer.cartChangedHeading');
  const triggerToast = useExceptionToast();

  const showSuccessNotification = () =>
    createToast(
      {
        theme: CartNotificationTheme.SUCCESS,
        rightSeperator: false,
        content: cartChangedHeading,
        subtext: cartChanged,
        clickIcon: 'times',
        icon: 'check-circle',
        timeOut: TOAST_TIMEOUT,
      },
      deleteToast,
    );

  const handleSecondaryButtonClick = async (profile: AmwayUserDetails) => {
    try {
      setSelectedProfile(profile);
      const previousOrderType = cartData.orderType;
      const res = await switchCart(cartData, OrderType.Proxy, profile as AmwayUserDetails);
      PublishEvent({
        cartEvent: CartEventsEnum.UpdateMiniCartIcon,
        cart: res,
      });
      const proxyCart = getActiveCart(res);
      const addressRes = await updateShippingAddressInLS(
        proxyCart.amwayUser?.amwayUserId as string,
        proxyCart.amwayUser?.accountId as string,
      );
      if (isProxyOrder(previousOrderType) && (addressRes === null || !addressRes?.contacts.length)) {
        //If api return empty res or fails while switching to proxy revert back to main user default address.
        const res = await updateShippingAddressInLS(
          cartData.amwayUser?.amwayUserId as string,
          cartData.amwayUser?.accountId as string,
        );
        if (res === null || res?.contacts.length === 0) {
          updateShippingAddressToEmpty();
        }
      }
      updateAddress();
      dispatch(updateCart(res));
      setShowAccountSearch(false);
      toggleDisplayAccountSearch?.(false);
      toggleDrawer?.();
      showSuccessNotification();
      onSwitchSuccess?.();
    } catch (error) {
      triggerToast();
    }
  };

  const handleContinueClick = async (orderType: string) => {
    if (orderType === OrderType.Proxy) {
      setShowAccountSearch(true);
      return;
    }

    if (cartData.orderType === OrderType.Gco && orderType === OrderType.Regular) {
      return;
    }

    if (orderType === OrderType.Gco) {
      return;
    }

    const res = (await switchToRegularCart(cartData, cartOverrideFlag)) as Cart;
    const addRes = await updateShippingAddressInLS(res.amwayUser.amwayUserId, res.amwayUser.accountId as string);
    if (addRes === null || addRes?.contacts.length === 0) {
      updateShippingAddressToEmpty();
    }
    updateAddress();
    dispatch(updateCart(res));
    PublishEvent({
      cartEvent: CartEventsEnum.UpdateMiniCartIcon,
      cart: res,
    });
    showSuccessNotification();
    toggleDrawer?.();
    onSwitchSuccess?.();
  };

  const handleAccountSearchPrimaryClick = () => {
    setShowAccountSearch(false);
    toggleDisplayAccountSearch?.(false);
    onRadioChange?.(cartData.orderType);
  };

  const handleModalConfirmClick = async (switchCartType?: SWITCH_PROXY_TYPE) => {
    const previousOrderType = cartData.orderType;
    const res = (await switchOrOverrideCart(
      getActiveCart(cartData),
      selectedProfile as AmwayUserDetails,
      switchCartType as SWITCH_PROXY_TYPE,
    )) as Cart;
    const proxyCart = getActiveCart(res);
    const addressRes = await updateShippingAddressInLS(
      proxyCart.amwayUser?.amwayUserId as string,
      proxyCart.amwayUser?.accountId as string,
    );
    if (isProxyOrder(previousOrderType) && (addressRes === null || !addressRes?.contacts.length)) {
      //If api return empty res or fails while switching to proxy revert back to main user default address.
      const res = await updateShippingAddressInLS(
        cartData.amwayUser?.amwayUserId as string,
        cartData.amwayUser?.accountId as string,
      );

      if (res === null || res?.contacts.length === 0) {
        updateShippingAddressToEmpty();
      }
    }
    updateAddress();
    dispatch(updateCart(res));
    setShowAccountSearch(false);
    PublishEvent({
      cartEvent: CartEventsEnum.UpdateMiniCartIcon,
      cart: res,
    });
    toggleDrawer?.();
    showSuccessNotification();
  };

  return (
    <>
      {showAccountSearch || displayAccountSearch ? (
        <AccountSearchDrawer
          showSwitchModal={true}
          showActiveCart={true}
          showDisableCards={!isGroupOrder(cartData.orderType)}
          onRadioChange={(profile) => handleSecondaryButtonClick(profile)}
          primaryButtonText={primaryButtonText}
          secondaryButtonText={secondaryButtonText}
          placeHolderText={inputPlaceHolderText}
          drawerHeaderTitle={searchDrawerHeaderTitle}
          subHeading={subHeadingText}
          onPrimaryButtonClick={handleAccountSearchPrimaryClick}
          modalConfirmClick={handleModalConfirmClick}
          cartData={cartData}
        />
      ) : (
        <Drawer closeIcon={true} closeOnBackdropClick bottomGap={0} onClose={onClose} header={searchDrawerHeaderTitle}>
          <div className={styles['cart-type-selection-drawer']}>
            <div className={styles['cart-type-selection-drawer-wrapper-text']}>{cartForText}</div>
            <CartTypeSelection
              onRadioChange={(res) => {
                onRadioChange?.(res);
                handleContinueClick(res);
              }}
              initalOrderType={initalOrderType}
              cartData={cartData}
            />
          </div>
        </Drawer>
      )}
    </>
  );
};
